import { IClient, Localization, LocalizedDistance, UserProfileId } from "@sp-crm/core";
import { AgentDirectorySearchBar } from "components/agent-directory/agent-directory-search-bar";
import { AgentDirectoryPickerList } from "components/agent-directory/agent-directory-search-list";
import { WhiteLabelRestricted } from "components/app/white-label-restricted";
import { useLocalizedStrings } from "components/locale-provider/locale-provider";
import { SearchRequestPagination } from "components/simple-search/search-request-pagination";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import { TextArea } from "components/ui/textarea";
import {
    useGetUserProfileQuery,
    useRequestClientTransferMutation,
    UserProfileStatus,
    useSearchUserProfilesQuery,
} from "generated/graphql";
import React, { useCallback, useMemo, useState } from "react";
import { ResponsiveMode } from "store/reducers/responsive";
import { useRegionId } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import {
    AgentDirectoryFilter,
    AgentDirectoryState,
    getInitialAgentDirectoryState,
} from "store/slices/agent-directory";

export const mapThreshold: ResponsiveMode = ResponsiveMode.xLarge;

interface SelectAgentForClientTransferProps {
    client: IClient;
    onTransferred: () => void;
}

const getInitialState = (client: IClient, locale: Localization) => {
    const initialState = getInitialAgentDirectoryState();
    const geocodeableRepresentation = client.geocodeableRepresentation();

    if (geocodeableRepresentation) {
        const geoParams = {
            address: client.bestLocationAddress ?? "",
            city: client.bestLocationCity ?? "",
            zip: client.bestLocationZip ?? "",
            county: "",
            state: client.bestLocationState?.toUpperCase() ?? "",
            matchType: "distance",
            distance: Math.ceil(locale.conversions.toMeters(50 as LocalizedDistance)),
        };
        return {
            ...getInitialAgentDirectoryState(),
            searchFilter: {
                ...initialState.searchFilter,
                geoParams,
            },
            searchRequest: {
                ...initialState.searchRequest,
                geoParams,
            },
        };
    }

    return initialState;
};

export const SelectAgentForClientTransfer: React.FC<
    SelectAgentForClientTransferProps
> = ({ client, onTransferred }) => {
    const publicProfileEnabled = useTenantSettings().settings.enablePublicProfile;
    const locale = useLocalizedStrings();
    const initialState = getInitialState(client, locale);

    const [newAppState, setNewAppState] = useState<AgentDirectoryState>(initialState);
    const regionId = useRegionId();

    const userProfileQuery = useGetUserProfileQuery(
        {},
        { enabled: publicProfileEnabled, keepPreviousData: true },
    );

    const userProfileComplete = useMemo(() => {
        if (!userProfileQuery.data) {
            return false;
        }

        return (
            userProfileQuery.data.getUserProfile.status === UserProfileStatus.Public &&
            userProfileQuery.data.getUserProfile.isComplete
        );
    }, [userProfileQuery.data]);

    const displayMode = useMemo(() => {
        if (!publicProfileEnabled) {
            return "none";
        }

        if (userProfileQuery.isLoading) {
            return "none";
        }

        return userProfileComplete ? "full" : "profile-incomplete";
    }, [publicProfileEnabled, userProfileComplete, userProfileQuery.isLoading]);

    const searchQuery = useSearchUserProfilesQuery(
        {
            params: {
                ...newAppState.searchRequest,
                regionId,
            },
        },
        {
            enabled: displayMode === "full" || displayMode === "profile-incomplete",
        },
    );

    const onSearchUpdated = useCallback((search: AgentDirectoryFilter) => {
        setNewAppState(prev => ({
            ...prev,
            searchFilter: search,
            searchRequest: {
                ...prev.searchRequest,
                query: search.search,
                geoParams: search.geoParams,
            },
        }));
    }, []);

    const handlePageChange = useCallback((page: number) => {
        setNewAppState(prev => ({
            ...prev,
            searchRequest: { ...prev.searchRequest, page },
        }));
    }, []);

    const handlePageSizeChange = useCallback((pageSize: number) => {
        setNewAppState(prev => ({
            ...prev,
            searchRequest: { ...prev.searchRequest, perPage: pageSize },
        }));
    }, []);

    const agentHits = useMemo(() => {
        return searchQuery.data?.searchUserProfiles.hits || [];
    }, [searchQuery.data?.searchUserProfiles.hits]);

    const [selectedAgent, setSelectedAgent] = React.useState<UserProfileId | null>(null);

    const [note, setNote] = React.useState<string>("");

    const requestClientTransfer = useRequestClientTransferMutation();

    const handleTransferClient = useCallback(async () => {
        await requestClientTransfer.mutateAsync({
            params: {
                clientId: client.id,
                toUserProfileId: selectedAgent,
                note,
            },
        });

        onTransferred();
    }, [requestClientTransfer, client.id, selectedAgent, note, onTransferred]);

    return (
        <WhiteLabelRestricted>
            {displayMode === "none" ? null : displayMode === "full" ||
              displayMode === "profile-incomplete" ? (
                <>
                    {!selectedAgent ? (
                        <AgentDirectorySearchBar
                            value={newAppState.searchFilter}
                            onChange={onSearchUpdated}
                            locationDropdown="left"
                        />
                    ) : null}

                    <div className="mt-2 lg:mt-4">
                        <div className="w-full flex">
                            <div className="full-width">
                                <AgentDirectoryPickerList
                                    agents={agentHits}
                                    value={selectedAgent}
                                    onChange={setSelectedAgent}
                                />
                                {searchQuery.data && !selectedAgent ? (
                                    <SearchRequestPagination
                                        request={newAppState.searchRequest}
                                        total={searchQuery.data.searchUserProfiles.total}
                                        onPageChange={handlePageChange}
                                        onPageSizeChange={handlePageSizeChange}
                                    />
                                ) : null}
                                {selectedAgent ? (
                                    <div className="mt-4">
                                        <TextArea
                                            label="Add a note"
                                            rows={5}
                                            value={note}
                                            onChange={e => setNote(e.target.value)}
                                        />

                                        <div className="flex items-center  space-x-2">
                                            <PrimaryButton
                                                disabled={requestClientTransfer.isLoading}
                                                onClick={handleTransferClient}>
                                                Transfer client
                                            </PrimaryButton>
                                            {requestClientTransfer.isLoading ? (
                                                <Spinner />
                                            ) : null}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </>
            ) : null}
        </WhiteLabelRestricted>
    );
};
